import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";

/**
 * Partner-analytics embed page. Bank IT teams drop this in an iframe:
 *   <iframe src="https://your-backend/embed/partner-analytics?partner_id=...&api_key=...">
 *
 * The page hits /api/public-embed/partner-analytics (key-protected) and
 * renders branded metric tiles. Designed to live in a 420×320 minimum frame.
 */
export default function PartnerAnalyticsEmbed() {
  const [params] = useSearchParams();
  const partnerId = params.get("partner_id") || "";
  const apiKey = params.get("api_key") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!partnerId || !apiKey) {
      setErr("Missing partner_id or api_key URL parameter.");
      return;
    }
    axios.get(`/api/public-embed/partner-analytics`, {
      params: { partner_id: partnerId, api_key: apiKey },
    })
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(e.response?.data?.detail || e.message || "Failed to load analytics");
      });
  }, [partnerId, apiKey]);

  if (err) {
    return (
      <div className="p-6 min-h-screen bg-white" data-testid="partner-analytics-error">
        <div className="border border-[#c15337]/25 bg-[#c15337]/8 text-[#8b3a23] rounded-md p-4 text-sm">
          {err}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-xs text-[#5c6360]" data-testid="partner-analytics-loading">Loading…</div>;
  }

  const primary = data.partner.primary_color || "#114232";
  const m = data.metrics;
  const tile = (label, value, accent) => (
    <div className="rounded-md p-4 border bg-white"
          style={{ borderColor: `${primary}24` }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold"
            style={{ color: accent || primary }}>{label}</div>
      <div className="font-heading text-2xl font-bold mt-1" style={{ color: primary }}>
        {value}
      </div>
    </div>
  );

  return (
    <div className="p-5 min-h-screen bg-[#f4f2eb]/50" data-testid="partner-analytics-embed">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] font-bold"
              style={{ color: primary }}>
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: primary }} />
          {data.partner.name} · Live metrics
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="partner-analytics-tiles">
          {tile("Merchants", m.merchant_count.toLocaleString())}
          {tile("Active policies", m.active_policies.toLocaleString(), "#c15337")}
          {tile("Attach rate", `${m.attach_rate_percent}%`, "#d49a36")}
          {tile("Paid claims", m.paid_claims.toLocaleString())}
          {tile("Open claims", m.open_claims.toLocaleString(), "#c15337")}
          {tile("Total payouts", `KES ${m.total_payouts_kes.toLocaleString()}`, "#d49a36")}
          {tile("Total policies", m.total_policies.toLocaleString())}
        </div>
        <div className="text-[10px] text-[#5c6360] mt-4 text-right">
          Generated {new Date(data.generated_at).toLocaleString()} · Powered by Fortiqa
        </div>
      </div>
    </div>
  );
}
